import { BaseSensorEntry } from 'types'

export interface PenAdditionPlan {
  /** Entries to append to the chart's selected pens, in the requested order. */
  additions: BaseSensorEntry[]
  /** Requested ids the site's tag list could not resolve. */
  unresolved: string[]
}

/**
 * Work out which of `sensorIds` can be added to the chart.
 *
 * Ids already plotted are skipped, and ids the site pool cannot resolve are
 * reported rather than dropped: a saved reference can outlive the datapoint it
 * names, and silently ignoring those makes an "add" look like a no-op.
 *
 * The pool arrives already indexed. A site's pool runs to thousands of entries
 * (6,400 at longridge), so building the index here would tie its cost to the
 * number of calls rather than to the pool itself.
 */
export function planPenAdditions(
  sensorIds: readonly string[],
  selectedSensors: readonly BaseSensorEntry[],
  poolById: ReadonlyMap<string, BaseSensorEntry>
): PenAdditionPlan {
  const selectedIds = new Set(selectedSensors.map((sensor) => sensor.id))

  const additions: BaseSensorEntry[] = []
  const unresolved: string[] = []
  const seen = new Set<string>()

  sensorIds.forEach((sensorId) => {
    if (selectedIds.has(sensorId) || seen.has(sensorId)) return
    seen.add(sensorId)

    const sensor = poolById.get(sensorId)
    if (sensor) additions.push(sensor)
    else unresolved.push(sensorId)
  })

  return { additions, unresolved }
}
